import { API_CONFIG } from '../config/constants';
import { auth } from '../config/firebase';

interface RequestOptions extends RequestInit {
  headers?: HeadersInit;
}

/**
 * Base API service with error handling and automatic Firebase token injection
 */
class ApiService {
  private baseURL: string;
  private timeout: number;

  constructor(baseURL: string = API_CONFIG.baseURL, timeout: number = API_CONFIG.timeout) {
    this.baseURL = baseURL;
    this.timeout = timeout;
  }

  /**
   * Get current Firebase ID token
   * @returns ID token or null if not authenticated
   */
  async getIdToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (user) {
      try {
        return await user.getIdToken();
      } catch (error) {
        console.error('Failed to get ID token:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Make HTTP request with automatic Firebase token injection for protected endpoints
   * @param endpoint - API endpoint
   * @param options - Fetch options
   * @returns Response data
   */
  async request<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Automatically add Firebase token to protected endpoints
    // Skip for /auth/login endpoint as it needs the token in the body
    if (!endpoint.includes('/auth/login')) {
      const token = await this.getIdToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const config: RequestInit = {
      ...options,
      headers,
      signal: controller.signal,
    };

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, config);
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data as T;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  /**
   * GET request
   */
  async get<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T = any>(endpoint: string, data?: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * PUT request
   */
  async put<T = any>(endpoint: string, data: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE request
   */
  async delete<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * POST request with FormData (for file uploads)
   */
  async postFormData<T = any>(endpoint: string, formData: FormData, options: RequestOptions = {}): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    // Get Firebase token
    const token = await this.getIdToken();
    const headers: Record<string, string> = {};
    if (token && !endpoint.includes('/auth/login')) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Don't set Content-Type header - let browser set it with boundary for FormData
    const config: RequestInit = {
      ...options,
      method: 'POST',
      headers,
      body: formData,
      signal: controller.signal,
    };

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, config);
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data as T;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }
}

const apiService = new ApiService();
export default apiService;
